import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Eye, Loader2, Plus, Rocket, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { KIND_LABEL, POOL_OPTIONS, addEntry, toPayload, zoneList } from "@/lib/decks";
import { useQuery } from "@tanstack/react-query";
import { useMyCardBacks } from "@/lib/queries";
import { VISIBILITIES } from "@/lib/mtg";
import { LAYOUT_PREFS } from "@/lib/schemes";
import { PageLoader } from "@/components/common/PageLoader";
import { EmptyState } from "@/components/common/PageParts";
import { CardSearchPanel } from "@/components/decks/CardSearchPanel";
import { DeckZones } from "@/components/decks/DeckZones";
import { DeckStats } from "@/components/decks/DeckStats";
import { DeckBackPicker } from "@/components/decks/DeckBackPicker";

function FormatSelect({ value, onChange }) {
  const { data = [] } = useQuery({ queryKey: ["formats"], queryFn: () => api.get("/formats").then((r) => r.data.items) });
  const groups = [
    ["Official formats", data.filter((f) => f.owner_id === "system" && f.rules?.official_legality)],
    ["Game modes & freeform", data.filter((f) => f.owner_id === "system" && !f.rules?.official_legality)],
    ["Community & my formats", data.filter((f) => f.owner_id !== "system")],
  ].filter(([, list]) => list.length);
  return (
    <select className="field w-auto text-xs" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Format" data-testid="deck-format-select">
      {groups.map(([label, list]) => <optgroup key={label} label={label}>{list.map((f) => <option key={f.format_id} value={f.format_id}>{f.name}</option>)}</optgroup>)}
    </select>
  );
}

function CustomToggle({ deck, onSave }) {
  if (!deck.format_summary.official_legality) return null;
  const on = deck.legality?.allow_custom;
  return (
    <label className="flex items-center gap-1.5 text-xs text-slate-300" title="Custom cards have no official legality. Turn this on if your playgroup allows them.">
      <input type="checkbox" checked={!!on} onChange={(e) => onSave({ allow_custom_cards: e.target.checked })} data-testid="deck-allow-custom-toggle" />Allow custom cards
    </label>
  );
}

function ZoneBacks({ deck, zones, onSave }) {
  const { data: backs = [] } = useMyCardBacks();
  const used = zones.filter((z) => deck.stats.zone_counts[z.key]);
  if (!used.length) return null;
  return (
    <div className="panel space-y-2 p-4" data-testid="deck-zone-backs">
      <p className="eyebrow text-[10px]">Per-zone card backs</p>
      {used.map((z) => (
        <label key={z.key} className="flex items-center justify-between gap-2 text-xs text-slate-300">{z.label}
          <select className="field w-40 py-1 text-xs" value={deck.zone_backs?.[z.key] || ""} data-testid={`deck-zone-back-${z.key}`}
            onChange={(e) => onSave({ ...(deck.zone_backs || {}), [z.key]: e.target.value || null })}>
            <option value="">Deck default</option>
            {backs.map((b) => <option key={b.card_back_id} value={b.card_back_id}>{b.name}</option>)}
          </select>
        </label>
      ))}
    </div>
  );
}

function CustomZones({ deck, onSave }) {
  const [label, setLabel] = useState("");
  const zones = deck.custom_zones || [];
  const add = (e) => {
    e.preventDefault();
    const key = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    if (!/^[a-z][a-z0-9_]{1,30}$/.test(key)) return toast.error("Zone name must start with a letter and be 2–31 characters");
    if (deck.available_zones.includes(key)) return toast.error("That zone already exists");
    onSave([...zones, { key, label: label.trim().slice(0, 40) }]);
    setLabel("");
  };
  return (
    <div className="panel space-y-2 p-4" data-testid="deck-custom-zones">
      <p className="eyebrow text-[10px]">Custom zones</p>
      {zones.map((z) => {
        const used = deck.stats.zone_counts[z.key];
        return (
          <div key={z.key} className="flex items-center justify-between text-xs text-slate-300" data-testid={`deck-custom-zone-${z.key}`}>
            <span>{z.label} <span className="font-mono text-slate-600">{z.key}</span></span>
            <button className="btn-ghost px-1.5 disabled:opacity-30" disabled={!!used} title={used ? "Move its cards out first" : "Remove zone"}
              onClick={() => onSave(zones.filter((x) => x.key !== z.key))} data-testid={`deck-custom-zone-remove-${z.key}`}><X className="h-3.5 w-3.5" /></button>
          </div>
        );
      })}
      <form onSubmit={add} className="flex gap-2">
        <input className="field py-1 text-xs" placeholder="e.g. Relics" maxLength={40} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="deck-custom-zone-input" />
        <button type="submit" className="btn-outline px-2 py-1 text-xs" disabled={zones.length >= 10} data-testid="deck-custom-zone-add"><Plus className="h-3.5 w-3.5" /></button>
      </form>
    </div>
  );
}

export default function DeckBuilder() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [deck, setDeck] = useState(null);
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState("saved");
  const [error, setError] = useState("");
  const pending = useRef(null);
  const { data: backs = [] } = useMyCardBacks();

  const apply = (data) => { setDeck(data); setEntries(data.entries); };
  useEffect(() => {
    api.get(`/decks/${deckId}`).then((r) => apply(r.data)).catch((e) => setError(formatApiError(e)));
  }, [deckId]);

  const save = async (patch) => {
    setStatus("saving");
    try {
      const { data } = await api.patch(`/decks/${deckId}`, patch);
      apply(data);
      setStatus("saved");
      qc.invalidateQueries({ queryKey: ["decks"] });
    } catch (err) {
      setStatus("error");
      toast.error(formatApiError(err));
    }
  };

  const changeEntries = (next) => {
    setEntries(next);
    setStatus("dirty");
    clearTimeout(pending.current);
    pending.current = setTimeout(() => save({ entries: toPayload(next) }), 700);
  };
  const updateEntry = (i, patch) => {
    const next = entries.map((e, j) => (j === i ? { ...e, ...patch } : e)).filter((e) => e.quantity > 0);
    changeEntries(next);
  };
  const publish = async () => {
    try {
      const { data } = await api.post(`/decks/${deckId}/${deck.published ? "unpublish" : "publish"}`);
      apply(data);
      toast.success(data.published ? "Deck published" : "Deck returned to draft");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (error) return <div className="container-x py-24"><EmptyState title="Deck unavailable" testid="deck-builder-error">{error}</EmptyState></div>;
  if (!deck) return <PageLoader />;
  const zones = zoneList(deck.available_zones, deck.custom_zones);

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-8 sm:px-6" data-testid="deck-builder-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4 border-b border-amber-700/30 pb-5">
        <div className="min-w-[260px] flex-1">
          <p className="eyebrow mb-2">Deck builder · {deck.format_label || deck.format} · {KIND_LABEL[deck.kind]}</p>
          <input className="w-full bg-transparent font-heading text-2xl font-bold text-amber-100 outline-none focus:text-aether-100 sm:text-3xl" defaultValue={deck.name}
            onBlur={(e) => e.target.value.trim() && e.target.value !== deck.name && save({ name: e.target.value.trim() })} aria-label="Deck name" data-testid="deck-name-input" />
          <p className="mt-1 font-mono text-xs text-slate-500" data-testid="deck-id">ID {deck.deck_id}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-slate-400" data-testid="deck-save-status">
            {status === "saving" || status === "dirty" ? <><Loader2 className="h-3 w-3 animate-spin" />Saving</> : status === "error" ? <span className="text-red-300">Not saved</span> : <><Check className="h-3 w-3 text-emerald-400" />Saved</>}
          </span>
          <select className="field w-auto text-xs capitalize" value={deck.visibility} onChange={(e) => save({ visibility: e.target.value })} aria-label="Visibility" data-testid="deck-visibility-select">
            {VISIBILITIES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <FormatSelect value={deck.format_summary.format_id} onChange={(id) => save({ format_id: id })} />
          <CustomToggle deck={deck} onSave={save} />
          <select className="field w-auto text-xs" value={deck.card_pool} onChange={(e) => save({ card_pool: e.target.value })} aria-label="Card pool" data-testid="deck-pool-select">
            {POOL_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <select className="field w-auto text-xs" value={deck.layout_preference || "mixed"} onChange={(e) => save({ layout_preference: e.target.value })} aria-label="Preferred layout" title="Preview preference only: any card layout can be added" data-testid="deck-layout-select">
            {Object.entries(LAYOUT_PREFS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Link to={`/decks/${deckId}`} className="btn-outline" data-testid="deck-view-link"><Eye className="h-4 w-4" />View</Link>
          <button className="btn-gold" onClick={publish} data-testid="deck-publish-button"><Rocket className="h-4 w-4" />{deck.published ? "Unpublish" : "Publish"}</button>
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-4 xl:col-span-3"><CardSearchPanel pool={deck.card_pool} zones={zones} formatKey={deck.format_summary.official_legality} formatName={deck.format_summary.name} onAdd={(card, zone) => changeEntries(addEntry(entries, card, zone))} /></div>
        <div className="min-w-0 lg:col-span-8 xl:col-span-6"><DeckZones entries={entries} onChange={updateEntry} zones={zones} backs={backs} issues={deck.legality?.card_issues} pinned={(deck.custom_zones || []).map((z) => z.key)} /></div>
        <div className="min-w-0 space-y-5 lg:col-span-12 xl:col-span-3">
          <DeckStats stats={deck.stats} warnings={deck.warnings} legality={deck.legality} />
          <DeckBackPicker value={deck.card_back_id} deckName={deck.name} onChange={(id) => save({ card_back_id: id })} />
          <ZoneBacks deck={deck} zones={zones} onSave={(zone_backs) => save({ zone_backs })} />
          <CustomZones deck={deck} onSave={(custom_zones) => save({ custom_zones })} />
          <button className="btn-ghost w-full text-xs text-red-300" data-testid="deck-delete-button" onClick={async () => {
            if (!window.confirm("Delete this deck?")) return;
            await api.delete(`/decks/${deckId}`).then(() => { qc.invalidateQueries({ queryKey: ["decks"] }); navigate("/decks"); }).catch((e) => toast.error(formatApiError(e)));
          }}>Delete deck</button>
        </div>
      </div>
    </div>
  );
}
